import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Desktop, WifiHigh, WifiSlash } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_META = {
  online:  { color: '#16A34A', label: 'Kiosk online',  icon: WifiHigh },
  stale:   { color: '#F59E0B', label: 'Kiosk warning', icon: WifiHigh },
  offline: { color: '#EF4444', label: 'Kiosk offline', icon: WifiSlash },
  none:    { color: '#6B7280', label: 'No kiosk connected', icon: Desktop },
};

function fmtAge(s) {
  if (s == null) return '';
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

/**
 * Compact status pill polling /api/events/:id/kiosks every 20s.
 * Shows green/yellow/red based on the freshest heartbeat among all registered kiosks.
 */
const KioskStatusPill = ({ eventId }) => {
  const [data, setData] = useState({ kiosks: [], online_count: 0, total_count: 0 });
  const [loaded, setLoaded] = useState(false);

  const fetchStatus = useCallback(async () => {
    if (!eventId) return;
    try {
      const res = await axios.get(`${BACKEND_URL}/api/events/${eventId}/kiosks`);
      setData(res.data || { kiosks: [] });
      setLoaded(true);
    } catch (_) {
      // Silent — pill hides if endpoint unreachable
    }
  }, [eventId]);

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 20_000);
    return () => clearInterval(id);
  }, [fetchStatus]);

  // Only render once we have data AND at least one kiosk has ever pinged
  if (!loaded || data.total_count === 0) return null;

  const best = data.kiosks[0];
  const statusKey = best ? best.status : 'none';
  const meta = STATUS_META[statusKey] || STATUS_META.none;
  const Icon = meta.icon;
  const freshAge = best ? fmtAge(best.age_seconds) : null;
  const pulses = statusKey === 'online';

  return (
    <div
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-semibold border"
      style={{
        background: 'rgba(255,255,255,0.02)',
        borderColor: `${meta.color}55`,
        color: meta.color,
      }}
      data-testid="kiosk-status-pill"
      data-status={statusKey}
      title={best ? `${best.hostname || 'kiosk'} · v${best.version}` : ''}
    >
      <span className="relative flex w-2 h-2">
        {pulses && (
          <span
            className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping"
            style={{ backgroundColor: meta.color }}
          />
        )}
        <span className="relative inline-flex w-2 h-2 rounded-full" style={{ backgroundColor: meta.color }} />
      </span>
      <Icon size={13} weight="bold" />
      <span>{meta.label}</span>
      {freshAge ? <span className="opacity-70 font-normal">· {freshAge}</span> : null}
      {data.total_count > 1 ? <span className="opacity-70 font-normal">· {data.online_count}/{data.total_count}</span> : null}
    </div>
  );
};

export default KioskStatusPill;
